import React, { createContext, useState } from "react";

// Local Imports
import { Page } from "components/shared/Page";
import { Header } from "components/shared/Header";
import { Tabs } from "components/shared/Tabs";
import { PlusIcon } from "@heroicons/react/24/outline";
import { Button } from "components/ui";

// ----------------------------------------------------------------------

export const SalesPageLayoutContext = createContext(null);

const DEFAULT_SALES_TABS = [
  { name: "Opportunities", path: "/sales/leads/opportunities" },
  { name: "Activities", path: "/sales/leads/activities" },
  { name: "Proposals", path: "/sales/leads/proposals" },
  { name: "Map", path: "/sales/leads/map" },
];

/**
 * Common layout for Sales module pages (Opportunities, Activities, Calendar, etc.)
 * Standardizes header, tabs, and table containers.
 */
export function SalesPageLayout({
  title,
  subtitle,
  titleSuffix,
  headerActions,
  primaryActionLabel,
  onPrimaryAction,
  children,
  modals,
  customToolbar,
  hideTabs = false,
  customTabs,
  unifiedHeader = true,
  belowTitleSlot,         // renders between the title row and the tabs
  absoluteSidePanel,      // panel rendered absolute top-0/right-0/bottom-0 inside the layout
  absolutePanelLeft,      // CSS left value for the absolute panel (e.g. "calc(var(--margin-x, 24px) + 200px)")
  absolutePanelClassName, // Tailwind classes for the absolute panel wrapper
  absolutePanelOpen,      // controls the slide-in animation state
}) {
  const [leftContainer, setLeftContainer] = useState(null);
  const [rightContainer, setRightContainer] = useState(null);
  const [mobileRightContainer, setMobileRightContainer] = useState(null);

  return (
    <SalesPageLayoutContext.Provider value={{ leftContainer, rightContainer, mobileRightContainer }}>
      <Page title={title}>
        <div className="transition-content relative flex h-full w-full flex-col overflow-hidden px-(--margin-x) pt-5 lg:pt-6">
          {unifiedHeader ? (
            /* ─── Unified Header ─────────────────────────────────────────────────
               Mobile  : Two stacked rows (title | actions)
               Tablet+ : Single row  (title … actions)
            ──────────────────────────────────────────────────────────────────── */
            <div className="relative z-[30] mb-2 shrink-0 font-sans">

              {/* Row 1 — always visible: Title + (on md+) left portal slots */}
              <div className="flex items-center justify-between gap-2 pb-0.5 sm:pb-1 sm:gap-4 relative">
                {/* Left side: title + any left-portal content */}
                <div className="flex min-w-0 flex-1 items-center gap-2 sm:gap-4">
                  <h1 className="shrink-0 text-base font-bold tracking-tight text-slate-900 dark:text-white sm:text-xl md:text-2xl">
                    {title}
                  </h1>
                  {titleSuffix}
                  {/* Left portal — hides on xs so actions go to row 2 */}
                  <div
                    ref={setLeftContainer}
                    className="hidden min-w-0 flex-1 items-center gap-3 sm:flex"
                  />
                </div>

                {/* Right side: search + toolbar portals + headerActions — hidden on xs */}
                <div className="hidden shrink-0 items-center gap-2 sm:flex">
                  <div ref={setRightContainer} className="flex flex-wrap items-center gap-2" />
                  {headerActions}
                  {onPrimaryAction && (
                    <Button
                      color="primary"
                      variant="filled"
                      onClick={onPrimaryAction}
                      className="flex h-8 items-center gap-2 rounded-md px-3 text-[10px] font-bold shadow-sm transition-colors md:h-9 md:px-4 md:text-sm"
                    >
                      <PlusIcon className="h-4 w-4 stroke-[2.5px]" />
                      <span>{primaryActionLabel}</span>
                    </Button>
                  )}
                </div>
              </div>
            </div>
          ) : (
            <Header
              title={title}
              subtitle={subtitle}
              actions={headerActions}
              primaryActionLabel={primaryActionLabel}
              onPrimaryAction={onPrimaryAction}
            />
          )}

          {belowTitleSlot && (
            <div className="relative z-[30] pointer-events-none [&>*]:pointer-events-auto">
              {belowTitleSlot}
            </div>
          )}

          {!hideTabs && (customTabs || <Tabs tabs={DEFAULT_SALES_TABS} />)}

          {/* Row 2 — mobile only: search + actions together on a single horizontal row below tabs */}
          {unifiedHeader && (
            <div className="flex items-center gap-1.5 pb-0.5 sm:hidden w-full overflow-x-auto no-scrollbar touch-pan-x py-0.5 mb-2 shrink-0 z-[30]">
              {/* Mobile right portal (search, column config, etc.) */}
              <div
                id="mobile-right-portal"
                ref={setMobileRightContainer}
                className="flex min-w-0 items-center gap-1.5 [&:not(:empty)]:flex-1"
              />
              {headerActions && (
                <div className="flex items-center gap-1.5 shrink-0">
                  {headerActions}
                </div>
              )}
              {onPrimaryAction && (
                <Button
                  color="primary"
                  variant="filled"
                  onClick={onPrimaryAction}
                  className="flex h-7.5 size-7.5 sm:size-auto sm:h-8 items-center justify-center gap-1.5 rounded-lg px-0 sm:px-3 text-xs font-bold shadow-sm transition-colors shrink-0"
                  title={primaryActionLabel}
                >
                  <PlusIcon className="h-4 w-4 stroke-[2.5px] shrink-0" />
                  <span className="hidden sm:inline">{primaryActionLabel}</span>
                </Button>
              )}
            </div>
          )}

          {customToolbar}
          <div className="min-h-0 flex-1 overflow-x-auto overflow-y-auto custom-scrollbar sales-page-scroll-container">
            {children}
          </div>

          {modals}

          {/* ── Absolute side panel ─────────────────────────────────────────────
              Anchored to the top of THIS div (which already starts below the navbar)
              so it naturally "touches" the navbar without z-index hacks.       ── */}
          <div
            className={`absolute top-0 bottom-0 right-0 z-[30] flex flex-col ${
              absolutePanelOpen ? "pointer-events-auto" : "pointer-events-none"
            } ${absolutePanelClassName || ""}`}
            style={absolutePanelLeft !== undefined ? { left: absolutePanelLeft } : {}}
          >
            {absoluteSidePanel}
          </div>
        </div>
      </Page>
    </SalesPageLayoutContext.Provider>
  );
}
